<template>
  <v-dialog v-model="dialog" :max-width="$vuetify.breakpoint.xsOnly ? '' : '600'" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title class="white--text">{{ $t('inventory.manage_warehouses') }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon dark :aria-label="$t('close')" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-5 gradient-icons">
        <v-row v-for="warehouse in warehouses" :key="warehouse.id" dense align="center">
          <v-col>
            <v-text-field
              v-model="warehouse.name"
              :label="$t('inventory.warehouse_name')"
              outlined
              dense
              hide-details
              @keyup.enter="save(warehouse)"
            >
              <template v-if="warehouse.id === defaultId" v-slot:append>
                <v-chip x-small color="primary" outlined>{{ $t('inventory.default_warehouse') }}</v-chip>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="auto" class="text-no-wrap">
            <v-btn
              icon
              color="primary"
              :disabled="!warehouse.name || warehouse.name.trim() === warehouse.savedName"
              :loading="savingId === warehouse.id"
              :aria-label="$t('save')"
              :title="$t('save')"
              @click="save(warehouse)"
            >
              <v-icon class="no-gradient">mdi-content-save</v-icon>
            </v-btn>
            <v-btn
              icon
              :disabled="warehouse.id === defaultId"
              :aria-label="$t('inventory.set_default_warehouse')"
              :title="$t('inventory.set_default_warehouse')"
              @click="setDefault(warehouse)"
            >
              <v-icon class="no-gradient">{{ warehouse.id === defaultId ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
            </v-btn>
            <v-btn
              icon
              :loading="deletingId === warehouse.id"
              :aria-label="$t('inventory.delete_warehouse')"
              :title="$t('inventory.delete_warehouse')"
              @click="remove(warehouse)"
            >
              <v-icon class="no-gradient">mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row dense align="center" class="mt-4">
          <v-col>
            <v-text-field
              v-model="newName"
              :label="$t('inventory.add_warehouse')"
              outlined
              dense
              hide-details
              @keyup.enter="add"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn
              icon
              color="primary"
              :disabled="!newName.trim()"
              :loading="adding"
              :aria-label="$t('inventory.add_warehouse')"
              :title="$t('inventory.add_warehouse')"
              @click="add"
            >
              <v-icon class="no-gradient">mdi-plus</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text :block="$vuetify.breakpoint.xsOnly" @click="dialog = false">{{ $t('close') }}</v-btn>
      </v-card-actions>
    </v-card>
    <confirm-dialog ref="confirm" />
  </v-dialog>
</template>

<script>
import inventoryApi from '@/api/inventory';
import ConfirmDialog from '@/components/ConfirmDialog';
import { defaultWarehouseId, rememberWarehouseId } from '@/helpers/selectedWarehouse';

export default {
  name: 'WarehouseManagementDialog',
  components: { ConfirmDialog },
  data() {
    return {
      dialog: false,
      warehouses: [],
      // Muistettu (tai ainoa) varasto, joka valitaan varastonhallintaan tultaessa
      defaultId: null,
      newName: '',
      savingId: null,
      adding: false,
      deletingId: null,
    };
  },
  methods: {
    async open() {
      this.newName = '';
      this.dialog = true;
      await this.load();
    },

    async load() {
      try {
        const warehouses = await inventoryApi.getWarehouses();
        this.warehouses = warehouses.map(w => ({ ...w, savedName: w.name }));
        this.defaultId = defaultWarehouseId(this.warehouses);
      } catch (error) {
        console.error('Error loading warehouses:', error);
        this.showError(this.$t('inventory.errors.load_warehouses'));
      }
    },

    async save(warehouse) {
      if (!warehouse.name || warehouse.name.trim() === warehouse.savedName) return;
      this.savingId = warehouse.id;
      try {
        await inventoryApi.updateWarehouse(warehouse.id, { name: warehouse.name.trim() });
        warehouse.savedName = warehouse.name.trim();
        this.showSuccess(this.$t('inventory.success.warehouse_saved'));
        this.$emit('changed');
      } catch (error) {
        console.error('Error saving warehouse:', error);
        this.showError(this.$t('inventory.errors.warehouse_save_failed'));
      } finally {
        this.savingId = null;
      }
    },

    async add() {
      if (!this.newName.trim()) return;
      this.adding = true;
      try {
        await inventoryApi.createWarehouse({ name: this.newName.trim() });
        this.newName = '';
        this.showSuccess(this.$t('inventory.success.warehouse_saved'));
        await this.load();
        this.$emit('changed');
      } catch (error) {
        console.error('Error adding warehouse:', error);
        this.showError(this.$t('inventory.errors.warehouse_save_failed'));
      } finally {
        this.adding = false;
      }
    },

    setDefault(warehouse) {
      rememberWarehouseId(warehouse.id);
      this.defaultId = warehouse.id;
      this.$emit('default-changed', warehouse.id);
    },

    async remove(warehouse) {
      const confirmed = await this.$refs.confirm.open(
        this.$t('inventory.delete_warehouse'),
        this.$t('inventory.confirm_delete_warehouse', { name: warehouse.savedName }),
        { color: 'error', agree: this.$t('inventory.delete_warehouse') }
      );
      if (!confirmed) return;
      this.deletingId = warehouse.id;
      try {
        await inventoryApi.deleteWarehouse(warehouse.id);
        this.showSuccess(this.$t('inventory.success.warehouse_deleted'));
        await this.load();
        this.$emit('changed');
      } catch (error) {
        console.error('Error deleting warehouse:', error);
        const inUse = error.response && error.response.status === 409;
        this.showError(this.$t(inUse ? 'inventory.errors.warehouse_in_use' : 'inventory.errors.warehouse_delete_failed'));
      } finally {
        this.deletingId = null;
      }
    },

    showSuccess(message) {
      this.$store.dispatch('showNotification', { color: 'success', message, timeOut: 2000 });
    },

    showError(message) {
      this.$store.dispatch('showNotification', { color: 'error', message, timeOut: 3000 });
    },
  },
};
</script>
